import Paho from "paho-mqtt";
import { v4 as uuidv4 } from "uuid";
import config from "../config.json";

/** @type {boolean} */
let _initialized = false;
/** @type {Paho.Client} */
let _client = null;
/** @type {string} */
let _clientId = null;
/** @type {string} */
let _alarmId = null;

/** @type {function(MqttEvent):void} */
let _onNewEvent = null;
/** @type {function(MqttAck):void} */
let _onNewAck = null;
/** @type {function(string):void} */
let _onNewStatus = null;

export class MqttEvent {
  /**
   * @param {string} mqttClientId
   * @param {string} eventId
   * @param {string} type
   * @param {string} [locationId]
   * @param {number} [time] Current time if not defined
   * @throws Throws error if required arguments are null or undefined
   */
  constructor(mqttClientId, eventId, type, locationId, time) {
    if (!mqttClientId) {
      throw new TypeError("mqttClientId is null!");
    } else {
      this.MqttClientId = mqttClientId;
    }

    if (!eventId) {
      throw new TypeError("eventId is null!");
    } else {
      this.EventId = eventId;
    }

    if (!type) {
      throw new TypeError("type is null!");
    } else {
      this.Type = type;
    }

    if (!locationId) {
      this.LocationId = "";
    } else {
      this.LocationId = locationId;
    }

    if (!time) {
      this.Time = Math.floor(Date.now() / 1000);
    } else {
      this.Time = time;
    }
  }

  /**
   * @param {string} json
   * @returns {(MqttEvent|null)} `Event` if successful, otherwise `null`
   */
  static fromJson(json) {
    try {
      let obj = JSON.parse(json);
      return new MqttEvent(
        obj.MqttClientId,
        obj.EventId,
        obj.Type,
        obj.LocationId,
        obj.Time
      );
    } catch (err) {
      console.error(err);
      return null;
    }
  }
}

export class MqttAck {
  /**
   * @param {string} mqttClientId
   * @param {string} eventId
   * @param {string} status
   * @param {number} [time] Current time if not defined
   * @throws Throws error if required arguments are null or undefined
   */
  constructor(mqttClientId, eventId, status, time) {
    if (!mqttClientId) {
      throw new TypeError("mqttClientId is null!");
    } else {
      this.MqttClientId = mqttClientId;
    }

    if (!eventId) {
      throw new TypeError("eventId is null!");
    } else {
      this.EventId = eventId;
    }

    if (!status) {
      throw new TypeError("eventId is null!");
    } else {
      this.Status = status;
    }

    if (!time) {
      this.Time = Math.floor(Date.now() / 1000);
    } else {
      this.Time = time;
    }
  }
  /**
   * @param {string} json
   * @returns {(MqttAck|null)} `Event` if successful, otherwise `null`
   */
  static fromJson(json) {
    try {
      let obj = JSON.parse(json);
      return new MqttAck(obj.MqttClientId, obj.EventId, obj.Status, obj.Time);
    } catch (err) {
      console.error(err);
      return null;
    }
  }
}

/**
 * @param {string} alarmId
 */
function initialize(alarmId) {
  if (_initialized) {
    return;
  }
  //_clientId = `${config.CLIENT_ID_PREFIX}-${alarmId}-${uuidv4()}`;
  _clientId = `${config.CLIENT_ID_PREFIX}-${alarmId}`;
  _client = new Paho.Client(
    config.BROKER_URL,
    config.BROKER_PORT,
    config.BROKER_PATH,
    _clientId
  );
  _client.onConnectionLost = _onConnectionLost;
  _client.onMessageArrived = _onMessageArrived;

  _alarmId = alarmId;

  _initialized = true;
}

/**
 * @param {Object} error
 * @param {number} error.errorCode
 * @param {string} error.errorMessage
 */
function _onConnectionLost(error) {
  if (error.errorCode !== 0) {
    console.log("Connection Lost: " + error.errorMessage);
  }
}

/**
 * @param {Paho.Message} message
 */
function _onMessageArrived(message) {
  let topic = message.destinationName;
  let messageType = topic.split("/").pop();
  if (messageType === "ack") {
    let ack = MqttAck.fromJson(message.payloadString);
    if (ack && _onNewAck) {
      let recId = ack.EventId.split(".")[0];
      if (recId === _alarmId) {
        _onNewAck(ack);
      }
    }
  } else if (messageType === "event") {
    let event = MqttEvent.fromJson(message.payloadString);
    if (event && _onNewEvent) {
      let recId = event.EventId.split(".")[0];
      if (recId === _alarmId) {
        _onNewEvent(event);
      }
    }
  }
  else if (messageType === "status") {
    let status = message.payloadString;
    if (_onNewStatus) {
      _onNewStatus(status);
    }
  }
}


function toggleConnection() {
  if (!_initialized) {
    return;
  }
  if (_client !== null) {
    if (_client.isConnected()) {
      disconnect();
    } else {
      connect();
    }
  }
}

/**
 * @param {string} username
 * @param {string} password
 */
function connect() {
  if (!_initialized) {
    return;
  }
  if (_client !== null && !_client.isConnected()) {
    _client.connect({
      userName: config.USERNAME,
      password: config.PASSWORD,
      onSuccess: _onConnectionSuccess,
      onFailure: _onConnectionFailure,
      useSSL: false,
    });
  }
}

function _onConnectionSuccess() {
  console.log("Connection Successfull!");
  _client.subscribe(`tjx/${config.ORGANIZATION_ID}/server/+/ack`);
  _client.subscribe(`tjx/${config.ORGANIZATION_ID}/server/+/status`);
  _client.subscribe(`tjx/${config.ORGANIZATION_ID}/server/+/event`);
}

function _onConnectionFailure() {
  console.log("Connection Failed!");
}

function disconnect() {
  if (!_initialized) {
    return;
  }
  if (_client !== null && _client.isConnected()) {
    _client.disconnect();
  }
}

/**
 * @param {string} topic
 * @param {string} message
 */
function _publish(topic, message) {
  if (_client.isConnected()) {
    _client.send(topic, message);
  }
}

/**
 * @param {string} type
 * @param {string} eventId
 */
function sendAlarm(type, eventId) {
  if (!_initialized) {
    return;
  }
  if (_client.isConnected()) {
    var topic = `tjx/${config.ORGANIZATION_ID}/client/${_client.clientId}/event`;
    var event = new MqttEvent(_client.clientId, `${_alarmId}.${eventId}`, type, _alarmId);
    var payload = JSON.stringify(event);
    _publish(topic, payload);
  }
}

/**
 * @param {function(MqttEvent):void} cb
 */
function setOnNewEventCallback(cb) {
  if (!_initialized) {
    return;
  }
  _onNewEvent = cb;
}

/**
 * @param {function(MqttAck):void} cb
 */
function setOnNewAckCallback(cb) {
  if (!_initialized) {
    return;
  }
  _onNewAck = cb;
}
/**
 * @param {function(string):void} cb
 */
function setOnNewStatusCallback(cb) {
  if (!_initialized) {
    return;
  }
  _onNewStatus = cb;
}

export default {
  initialize,
  toggleConnection,
  connect,
  disconnect,
  sendAlarm,
  setOnNewEventCallback,
  setOnNewAckCallback,
  setOnNewStatusCallback,
};
